import React from 'react';

export function toSentenceCase(str: string) {
  return str.toLowerCase().charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function toTitleCase(str: string) {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export function replaceLineBreaks(children: React.ReactNode): React.ReactNode {
  if (!children) return children;

  if (typeof children === 'string') {
    const parts = children.split('\\n');
    return parts.reduce<React.ReactNode[]>((acc, part, idx) => {
      if (idx > 0) acc.push(<br key={idx} />);
      acc.push(part);
      return acc;
    }, []);
  }

  if (Array.isArray(children)) {
    return children.map((child, idx) => (
      <React.Fragment key={idx}>{replaceLineBreaks(child)}</React.Fragment>
    ));
  }

  if (React.isValidElement(children)) {
    const props = { ...children.props };
    if (props.children) {
      props.children = replaceLineBreaks(props.children);
    }
    return React.cloneElement(children, props);
  }

  return children;
}

/**
 * Characters to convert to superscript.
 * Extend this map if you need more.
 */
const SPECIAL_CHARS = new Set([
  '©', // copyright
  '™', // trademark
  '®', // registered
  '†', // dagger
  '‡', // double dagger
  '§', // section
  '¶', // pilcrow
  '°', // degree
  '‰', // per mille
  'µ', // micro
  '※', // reference mark
  '℠', // service mark
  '℗' // sound recording copyright
]);

/** Tailwind classes that mimic <sup> styling */
const SUP_CLASS = 'align-super font-medium';

/**
 * Recursively walks any React tree and wraps the target characters
 * in <span className="align-super text-xs">...</span>
 */
export function superscriptSpecialChars(
  node: React.ReactNode,
  inheritedClasses = ''
): React.ReactNode {
  // Plain text: split into individual characters
  if (typeof node === 'string') {
    return [...node].map((ch, idx) =>
      SPECIAL_CHARS.has(ch) ? (
        <span key={idx} className={`${inheritedClasses} ${SUP_CLASS}`.trim()}>
          {ch}
        </span>
      ) : (
        ch
      )
    );
  }

  // Valid React element: clone with processed children
  if (React.isValidElement(node)) {
    const ownClasses: string = node.props.className ?? '';
    const nextInherited = [inheritedClasses, ownClasses]
      .filter(Boolean)
      .join(' ');

    const children = React.Children.map(node.props.children, (child) =>
      superscriptSpecialChars(child, nextInherited)
    );
    return React.cloneElement(node, undefined, children);
  }

  // Array of nodes: process each one
  if (Array.isArray(node)) {
    return node.map((child, i) => (
      <React.Fragment key={i}>{superscriptSpecialChars(child)}</React.Fragment>
    ));
  }

  return node;
}
